import { useEffect, useId, useState } from 'react';
import { useT } from '../../i18n';
import { errorMessage } from '../../i18n/errorMessage';
import { fetchMyProfile, updateMyProfile, type MyProfile } from '../../lib/api/myPlayer';
import { safeId } from '../ui/labelledControl';

// ═══════════════════════════════════════════════════════════════════
// «MIN PROFIL» — THE PLAYER EDITS THEIR OWN IDENTITY (#796).
//
// Decided 2026-10-23: a player with an account changes their own name, profile
// address, phone and gender; clubs may not (`player_self_managed`), and support
// can when the person cannot. Until this existed nobody but support could.
//
// THE ADDRESS HERE IS THE PROFILE'S, what a club sees of its member. The
// sign-in address is the account's and is not changed by this form — the
// screen says so, because the two look alike.
// ═══════════════════════════════════════════════════════════════════

export interface MyProfileEditorProps {
  /** Injectable for tests. */
  load?: typeof fetchMyProfile;
  save?: typeof updateMyProfile;
}

export function MyProfileEditor({
  load = fetchMyProfile,
  save = updateMyProfile,
}: MyProfileEditorProps) {
  const t = useT();
  const uid = safeId('my-profile', useId());
  const ids = {
    title: `${uid}-title`,
    name: `${uid}-name`,
    email: `${uid}-email`,
    emailNote: `${uid}-email-note`,
    phone: `${uid}-phone`,
    gender: `${uid}-gender`,
  };
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'none' }
    | { kind: 'ready' }
    | { kind: 'saving' }
    | { kind: 'saved' }
    | { kind: 'failed'; message: string }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void load().then((res) => {
      if (cancelled) return;
      if (res.state === 'ok' && res.profile) {
        setProfile(res.profile);
        setState({ kind: 'ready' });
      } else if (res.state === 'ok') setState({ kind: 'none' });
      else setState({ kind: 'failed', message: t('account.myProfile.unreachable') });
    });
    return () => {
      cancelled = true;
    };
    // Once per mount: the profile is the account's, and the account does not
    // change under a mounted screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.kind === 'loading') {
    return (
      <p className="text-sm text-ink-3" role="status">
        {t('account.myProfile.loading')}
      </p>
    );
  }
  if (state.kind === 'none' || !profile) {
    return state.kind === 'failed' ? (
      <p className="text-sm text-rose-700" role="alert">
        {state.message}
      </p>
    ) : null;
  }

  const set = (patch: Partial<MyProfile>) => {
    setProfile({ ...profile, ...patch });
    if (state.kind === 'saved' || state.kind === 'failed') setState({ kind: 'ready' });
  };

  const submit = async () => {
    if (!profile.name.trim()) {
      setState({ kind: 'failed', message: t('account.myProfile.nameRequired') });
      return;
    }
    setState({ kind: 'saving' });
    const res = await save(profile);
    if (res.state === 'ok') {
      if (res.profile) setProfile(res.profile);
      setState({ kind: 'saved' });
    } else {
      setState({
        kind: 'failed',
        message:
          res.state === 'refused'
            ? errorMessage(t, res.reason)
            : t('account.myProfile.unreachable'),
      });
    }
  };

  return (
    <section aria-labelledby={ids.title} data-testid="my-profile">
      <h2 id={ids.title} className="font-semibold text-sm">
        {t('account.myProfile.title')}
      </h2>
      <form
        className="space-y-3 mt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div>
          <label htmlFor={ids.name} className="block text-[11px] text-ink-3">
            {t('account.myProfile.name')}
          </label>
          <input
            id={ids.name}
            className="input w-full"
            value={profile.name}
            autoComplete="name"
            onChange={(e) => set({ name: e.target.value })}
            data-testid="my-profile-name"
          />
        </div>
        <div>
          <label htmlFor={ids.email} className="block text-[11px] text-ink-3">
            {t('account.myProfile.email')}
          </label>
          <input
            id={ids.email}
            type="email"
            className="input w-full"
            value={profile.email}
            autoComplete="email"
            aria-describedby={ids.emailNote}
            onChange={(e) => set({ email: e.target.value })}
            data-testid="my-profile-email"
          />
          <p id={ids.emailNote} className="text-[11px] text-ink-3 mt-1">
            {t('account.myProfile.emailNote')}
          </p>
        </div>
        <div>
          <label htmlFor={ids.phone} className="block text-[11px] text-ink-3">
            {t('account.myProfile.phone')}
          </label>
          <input
            id={ids.phone}
            type="tel"
            className="input w-full"
            value={profile.phone}
            autoComplete="tel"
            onChange={(e) => set({ phone: e.target.value })}
            data-testid="my-profile-phone"
          />
        </div>
        <div>
          <label htmlFor={ids.gender} className="block text-[11px] text-ink-3">
            {t('account.myProfile.gender')}
          </label>
          <select
            id={ids.gender}
            className="input w-full"
            value={profile.gender}
            onChange={(e) => set({ gender: e.target.value as MyProfile['gender'] })}
            data-testid="my-profile-gender"
          >
            <option value="">{t('account.myProfile.genderUnset')}</option>
            <option value="M">{t('account.myProfile.genderMale')}</option>
            <option value="F">{t('account.myProfile.genderFemale')}</option>
          </select>
        </div>
        {state.kind === 'failed' && (
          <p className="text-sm text-rose-700" role="alert" data-testid="my-profile-failed">
            {state.message}
          </p>
        )}
        {state.kind === 'saved' && (
          <p className="text-sm text-ink-2" role="status" data-testid="my-profile-saved">
            {t('account.myProfile.saved')}
          </p>
        )}
        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={state.kind === 'saving'}
          data-testid="my-profile-save"
        >
          {state.kind === 'saving' ? t('account.myProfile.saving') : t('account.myProfile.save')}
        </button>
      </form>
    </section>
  );
}
